<template>
  <div class="field no-select">
    <label
      v-if="label"
      class="mb-2 label no-select"
      :for="name"
    >
      <h3>
        {{ label }}
      </h3>
    </label>
    <p
      v-if="hasHelpText"
      class="help-text"
      v-html="finalHelpText"
    ></p>
    <div
      ref="html-editor"
      class="html-editor ui-type-area"
      :class="{ 'read-only': isViewerUser }"
    ></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { basicSetup, EditorView } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';

import commonProperties from './common-properties';

export default {
  name: 'HTMLEditor',
  mixins: [commonProperties],
  inheritAttrs: false,
  props: {
    language: {
      type: String,
      default: 'html',
    },
  },
  data() {
    return {
      languages: {
        html,
        css,
      },
    };
  },
  computed: {
    ...mapGetters(['isViewerUser']),
  },
  mounted() {
    const theme = EditorView.theme({
      '&': {
        width: '100%',
        height: '100%',
        border: '1px solid #d1d1d1',
        borderRadius: '4px',
        overflow: 'hidden', // avoid sharp edges
      },
      '&.cm-focused': { outline: 'none' },
      '.cm-scroller': { overflow: 'auto' },
    });

    const editor = new EditorView({
      doc: this.model,
      extensions: [
        theme,
        EditorView.lineWrapping,
        EditorState.readOnly.of(this.isViewerUser),
        basicSetup,
        this.selectedLanguage(this.language),
      ],
      parent: this.$refs['html-editor'],
      dispatch: (tr) => {
        editor.update([tr]);
        if (tr.changes.empty) {
          return;
        }
        const newValue = editor.state.doc.toString();
        this.model = newValue;
      },
    });
  },
  methods: {
    selectedLanguage(language) {
      return this.languages[language]();
    },
  },
};
</script>

<style scoped lang="scss">
.field {
  overflow: hidden;
}

h3 {
  font-weight: $lb-medium;
  font-size: $size-normal;
  line-height: 1.5;
  color: $dark;
}

div.html-editor {
  height: 240px;

  ::v-deep .cm-scroller {
    outline: 1px solid red;

    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 100px;
    }
    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 100px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: $lb-medium-gray;
    }
  }
}

.help-text {
  margin-top: 0;
  margin-bottom: 1em;
}

.read-only {
  position: relative;
  color: ls('gray-500');

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 10;
    cursor: not-allowed;
  }
}
</style>
